import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { authService } from '../../services/authService';
import { SchedulerWizardHowTo, SCHEDULER_WIZARD_STAGES } from './SchedulerWizardHowTo';
import { useAppDialog } from './AppDialog';
import './SchedulerWizard.css';

export type WizardReadiness = 'Not started' | 'Needs attention' | 'Ready' | 'Not required';
interface Props {
  stage: number;
  reached: number;
  readiness: WizardReadiness[];
  summaries?: string[];
  busy: boolean;
  committed: boolean;
  publicationUncertain?: boolean;
  error: string;
  children: ReactNode;
  onStage: (stage: number) => void;
  onNext: () => void;
  onClose: () => void;
}

export function SchedulerWizardShell(props: Props) {
  const finalStage = SCHEDULER_WIZARD_STAGES.length - 1;
  const { stage, reached, readiness, summaries, busy, committed, publicationUncertain, error, children, onStage, onNext, onClose } = props;
  const panel = useRef<HTMLDivElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const appDialog = useAppDialog();
  const content = useRef<HTMLDivElement>(null);
  const [help, setHelp] = useState(false);
  const titleId = useId();
  const descriptionId = useId();
  const helpId = useId();
  const busyRef = useRef(busy);
  const committedRef = useRef(committed);
  const closeRef = useRef(onClose);
  busyRef.current = busy;
  committedRef.current = committed;
  closeRef.current = onClose;
  const [themeSource] = useState(() => {
    const trigger = document.activeElement;
    return trigger instanceof HTMLElement && trigger.closest('.tenant-portal')
      || document.querySelector('.tenant-portal') || document.documentElement;
  });
  const readTheme = () => {
    const style = getComputedStyle(themeSource);
    const tokens: Record<string, string> = { fontFamily: style.fontFamily };
    for (const name of Array.from(style).filter(name => name.startsWith('--'))) {
      const value = style.getPropertyValue(name).trim();
      if (value) tokens[name] = value;
    }
    return tokens as CSSProperties;
  };
  const [theme, setTheme] = useState(readTheme);
  useEffect(() => {
    const observer = new MutationObserver(() => setTheme(readTheme()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, [themeSource]);

  useEffect(() => {
    const previous = document.activeElement;
    const overlay = panel.current?.parentElement;
    const siblings = Array.from(document.body.children).filter((node): node is HTMLElement =>
      node instanceof HTMLElement && node !== overlay);
    const inert = siblings.map(node => node.inert);
    const overflow = document.body.style.overflow;
    siblings.forEach(node => { node.inert = true; });
    document.body.style.overflow = 'hidden';
    const focusInside = (event: FocusEvent) => {
      if (!panel.current?.contains(event.target as Node)) heading.current?.focus();
    };
    const warnReload = (event: BeforeUnloadEvent) => {
      if (committedRef.current && !busyRef.current) return;
      event.preventDefault();
      event.returnValue = '';
    };
    document.addEventListener('focusin', focusInside);
    window.addEventListener('beforeunload', warnReload);
    const initial = authService.getState();
    const unsubscribe = authService.subscribe(() => {
      const current = authService.getState();
      if (current.user?.tenantId !== initial.user?.tenantId || current.user?.id !== initial.user?.id ||
        current.isAuthenticated !== initial.isAuthenticated) closeRef.current();
    });
    heading.current?.focus();
    return () => {
      unsubscribe();
      document.removeEventListener('focusin', focusInside);
      window.removeEventListener('beforeunload', warnReload);
      siblings.forEach((node, index) => { node.inert = inert[index]; });
      document.body.style.overflow = overflow;
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
    };
  }, []);
  useEffect(() => {
    heading.current?.focus();
    panel.current?.querySelector('main')?.scrollTo?.(0, 0);
  }, [stage]);
  useEffect(() => {
    if (error) panel.current?.querySelector<HTMLElement>('[role="alert"]')?.focus();
  }, [error]);
  useEffect(() => {
    if (content.current) content.current.inert = confirmClose;
  }, [confirmClose]);
  const requestClose = () => {
    if (busyRef.current || confirmClose) return;
    if (committed) onClose();
    else {
      const previous = document.activeElement;
      setConfirmClose(true);
      void appDialog.confirm(publicationUncertain
        ? 'Publication may have completed. Closing loses this request identity and its safe retry. Keep this wizard open to recover the result.'
        : 'No schedule has been published. This draft will be lost.', {
        title: 'Discard unsaved schedule?', cancelLabel: 'Keep editing', confirmLabel: 'Discard draft', danger: true,
      }).then(discard => {
        setConfirmClose(false);
        if (discard) onClose();
        else if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
      });
    }
  };

  return createPortal(<div className="scheduler-wizard-overlay" style={theme}
    onMouseDown={event => { if (event.currentTarget === event.target) requestClose(); }}>
    <div className="scheduler-wizard" ref={panel} role="dialog" aria-modal="true"
      aria-labelledby={titleId} aria-describedby={descriptionId} aria-busy={busy}
      onKeyDown={event => {
        if (event.key === 'Escape') {
          if (confirmClose) return;
          event.stopPropagation();
          requestClose();
        }
        if (event.key === 'Tab') {
          const focusScope = confirmClose ? panel.current?.querySelector('.app-dialog') : panel.current;
          const controls = Array.from(focusScope?.querySelectorAll<HTMLElement>(
            'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href]') ?? [])
            .filter(node => !node.closest('[hidden]'));
          const first = controls[0], last = controls[controls.length - 1];
          if (event.shiftKey && (document.activeElement === first || document.activeElement === heading.current)) {
            event.preventDefault(); last?.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault(); first?.focus();
          }
        }
      }}>
      <header>
        <div className="wizard-heading-row">
          <h2 id={titleId} ref={heading} tabIndex={-1}>Create schedule - {SCHEDULER_WIZARD_STAGES[stage]}</h2>
          <button className="btn-secondary wizard-howto" type="button" aria-expanded={help}
            aria-controls={helpId} disabled={confirmClose}
            onClick={() => setHelp(value => !value)}>HowTo for this step</button>
        </div>
        <p id={descriptionId}>Complete required stages in order. Back keeps your draft; reload discards it.</p>
        <ol className="wizard-stepper" aria-label="Schedule creation readiness">
          {SCHEDULER_WIZARD_STAGES.map((name, index) => <li key={name}>
            <button type="button" aria-current={stage === index ? 'step' : undefined}
              className={readiness[index] === 'Ready' || readiness[index] === 'Not required' ? 'wizard-step-complete' : undefined}
              disabled={busy || confirmClose || index > reached || (committed && index !== finalStage) ||
                (!committed && index === finalStage) || (!committed && index > stage &&
                  !readiness.slice(0, index).every(value => value === 'Ready' || value === 'Not required'))}
              onClick={() => onStage(index)}>
              <span className="wizard-step-name"><span className="wizard-step-counter">{index + 1}.</span> {name}</span>
              <span className="wizard-step-summary" title={readiness[index] === 'Ready' || readiness[index] === 'Not required' ? summaries?.[index] : undefined}>
                {(readiness[index] === 'Ready' || readiness[index] === 'Not required') && summaries?.[index]}
              </span>
              <span className="wizard-step-readiness">{readiness[index]}</span>
            </button>
          </li>)}
        </ol>
      </header>
      <main>
        <div ref={content}>
          <div id={helpId} hidden={!help} className="wizard-help-card">
            {help && <SchedulerWizardHowTo stage={stage} />}
          </div>
          {error && <div role="alert" tabIndex={-1}>{error}</div>}
          {children}
        </div>
      </main>
      <footer>
        {!committed && <button className="wizard-back btn-secondary" type="button" disabled={stage === 0 || busy || confirmClose} onClick={() => onStage(stage - 1)}>Back</button>}
        <button className="wizard-cancel btn-secondary" type="button" disabled={busy || confirmClose} onClick={requestClose}>{committed ? 'Close' : 'Cancel'}</button>
        {stage < finalStage && <button className="wizard-next btn-primary" type="button" disabled={busy || confirmClose ||
          (readiness[stage] !== 'Ready' && readiness[stage] !== 'Not required')} onClick={onNext}>
          {busy ? 'Working...' : stage === finalStage - 1 ? 'Publish schedule' : 'Next'}
        </button>}
      </footer>
      {appDialog.dialog}
    </div>
  </div>, document.body);
}
